"use client";

import { useState, useRef, useEffect } from "react";
import { eventBus } from "@/lib/utils/eventBus";
import type { AtlasSearchProcess } from "@/lib/atlas/integration";

/** Swipe-up Atlas expansion — same behavior as UltraNavigationBar. */
export function useAtlasNavExpansion(atlasProcess: AtlasSearchProcess | null) {
  const [atlasExpansionState, setAtlasExpansionState] = useState<0 | 1 | 2>(0);
  const [swipeProgress, setSwipeProgress] = useState(0);
  const atlasContainerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const didMoveRef = useRef(false);

  useEffect(() => {
    return eventBus.on("MINDALOO:ATLAS_FOCUS", () => setAtlasExpansionState(2));
  }, []);

  useEffect(() => {
    if (!atlasProcess || !atlasContainerRef.current) return;

    const container = atlasContainerRef.current;
    const swipeThreshold = 80;

    const handleStart = (clientY: number) => {
      startYRef.current = clientY;
      isDraggingRef.current = true;
      didMoveRef.current = false;
    };

    const handleMove = (clientY: number) => {
      if (!isDraggingRef.current || startYRef.current === null) return;
      const deltaY = startYRef.current - clientY;
      if (Math.abs(deltaY) < 8) return; // ignore micro-movements (clicks)
      didMoveRef.current = true;

      if (deltaY > 10 && atlasExpansionState === 0) {
        const progress = Math.min(deltaY / swipeThreshold, 1);
        setSwipeProgress(progress);
        if (progress > 0.5) {
          setAtlasExpansionState(2);
          setSwipeProgress(1);
        }
      } else if (deltaY < -10 && atlasExpansionState === 2) {
        const swipeDown = Math.abs(deltaY);
        const progress = Math.max(1 - swipeDown / swipeThreshold, 0);
        setSwipeProgress(progress);
        if (progress < 0.3) {
          setAtlasExpansionState(0);
          setSwipeProgress(0);
        }
      }
    };

    const handleEnd = () => {
      if (isDraggingRef.current) {
        // Only change state if user actually swiped (not just clicked a button)
        if (didMoveRef.current) {
          if (atlasExpansionState === 0 && swipeProgress > 0.3) {
            setAtlasExpansionState(2);
          } else if (atlasExpansionState === 2 && swipeProgress < 0.7) {
            setAtlasExpansionState(0);
          }
        }
        setSwipeProgress(0);
        isDraggingRef.current = false;
        didMoveRef.current = false;
        startYRef.current = null;
      }
    };

    const handleTouchStart = (e: TouchEvent) => handleStart(e.touches[0].clientY);
    const handleTouchMove = (e: TouchEvent) => handleMove(e.touches[0].clientY);
    const handleTouchEnd = () => handleEnd();

    const handleMouseDown = (e: MouseEvent) => {
      handleStart(e.clientY);
      const handleMouseMove = (ev: MouseEvent) => handleMove(ev.clientY);
      const handleMouseUp = () => {
        handleEnd();
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    container.addEventListener("touchstart", handleTouchStart);
    container.addEventListener("touchmove", handleTouchMove);
    container.addEventListener("touchend", handleTouchEnd);
    container.addEventListener("mousedown", handleMouseDown);

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("mousedown", handleMouseDown);
    };
  }, [atlasProcess, atlasExpansionState, swipeProgress]);

  return {
    atlasExpansionState,
    setAtlasExpansionState,
    atlasContainerRef,
  };
}
